// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import Minimize from "~icons/ph/arrows-in-simple";
import Expand from "~icons/ph/arrows-out-simple";
import CameraRotate from "~icons/ph/camera-rotate";
import MessageCircle from "~icons/ph/chat-circle";
import Mic from "~icons/ph/microphone";
import MicOff from "~icons/ph/microphone-slash";
import MonitorUp from "~icons/ph/monitor-arrow-up";
import Phone from "~icons/ph/phone";
import PhoneOff from "~icons/ph/phone-slash";
import Float from "~icons/ph/picture-in-picture";
import Square from "~icons/ph/square";
import Video from "~icons/ph/video-camera";
import VideoOff from "~icons/ph/video-camera-slash";
import Volume2 from "~icons/ph/speaker-high";
import VolumeX from "~icons/ph/speaker-slash";
import X from "~icons/ph/x";
import { Avatar, Modal, Wave } from "../ui/components";
import { AudioMeter } from "./audio-meter";
import { CallControl } from "./call-control";
import { isAnswered, type Call, type EndedCall } from "./call-state";
import { CallPortrait, CallVideo, VideoPreview } from "./call-video";
import { useCallPictureInPicture } from "./use-call-picture-in-picture";
import { duration, t } from "../i18n";
import type { CallMedia, Person } from "../../shared/types";
import type { CallSession } from "./call-session";

type Corner = "top-start" | "top-end" | "bottom-start" | "bottom-end";

// Over a moving picture on a phone, the controls fade until someone touches the
// screen; CSS decides where this applies.
function useControlsIdle(watching: boolean) {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    if (!watching) return;
    let timer: ReturnType<typeof setTimeout>;
    const wake = () => {
      setIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setIdle(true), 4000);
    };
    wake();
    const activity = ["pointerdown", "pointermove", "keydown"];
    for (const type of activity) document.addEventListener(type, wake);
    return () => {
      clearTimeout(timer);
      for (const type of activity) document.removeEventListener(type, wake);
    };
  }, [watching]);
  return watching && idle;
}

export function CallModal({
  session,
  call,
  ended,
  friend,
  minimized,
  onMinimize,
  onExpand,
  onCallAgain,
  onMessage,
}: {
  session: CallSession;
  call: Call | null;
  // The call that just ended.
  ended: EndedCall | null;
  // The person on this call or the one that just ended, if they are a friend.
  friend: Person | undefined;
  minimized: boolean;
  onMinimize: () => void;
  onExpand: () => void;
  onCallAgain: (person: Person, media: CallMedia) => void;
  onMessage: (person: Person) => void;
}) {
  // Keyed by call so a pending question never carries into the next call.
  const [confirmingEnd, setConfirmingEnd] = useState<string | null>(null);
  const [corner, setCorner] = useState<Corner>("bottom-end");
  const answered = isAnswered(call);
  const pictureInPicture = useCallPictureInPicture(
    answered ? call.id : null,
    !!call &&
      (call.media === "video" ||
        call.remoteSharingScreen ||
        !call.remoteCameraOff),
  );
  const controlsIdle = useControlsIdle(
    !minimized &&
      call?.status === "connected" &&
      !!call.remoteStream &&
      !call.remoteCameraOff,
  );
  if (!call)
    return ended ? (
      <CallEnded
        ended={ended}
        friend={friend}
        corner={corner}
        minimized={minimized}
        onClose={session.dismissEnded}
        onCallAgain={onCallAgain}
        onMessage={onMessage}
      />
    ) : null;
  // Ending an answered call-link call uses up the pass between them, so it asks
  // first. Ringing calls cancel or decline at once, and a friend can call again.
  const hangUp = () =>
    answered && !friend ? setConfirmingEnd(call.id) : void session.end();
  const confirm = !!call.id && confirmingEnd === call.id && (
    <Modal
      title={t("إنهاء المكالمة مع {name}؟", { name: call.person.name })}
      onClose={() => setConfirmingEnd(null)}
      className="end-call-confirm"
    >
      <h2>{t("إنهاء المكالمة مع {name}؟", { name: call.person.name })}</h2>
      <div className="confirm-actions">
        <button
          className="button secondary"
          onClick={() => setConfirmingEnd(null)}
        >
          {t("رجوع")}
        </button>
        <button
          className="button danger-button"
          onClick={() => void session.end()}
        >
          <PhoneOff width={16} height={16} />
          {t("إنهاء الآن")}
        </button>
      </div>
    </Modal>
  );
  const float = pictureInPicture.supported
    ? () => void pictureInPicture.open()
    : undefined;
  const floatingError = pictureInPicture.failed && (
    <p className="call-pip-error" role="status">
      {t(
        "تعذر عرض المكالمة فوق التبويبات. اسمح بصورة داخل صورة في إعدادات المتصفح وحاول مجددًا.",
      )}
    </p>
  );
  if (pictureInPicture.pipWindow)
    return createPortal(
      <div className="call-pip-content">
        <CallMini
          session={session}
          call={call}
          corner={corner}
          onMove={setCorner}
          onExpand={() => {
            window.focus();
            pictureInPicture.close();
            onExpand();
          }}
          onHangUp={hangUp}
          floating
        />
        {confirm}
      </div>,
      pictureInPicture.pipWindow.document.body,
    );
  if (minimized)
    return (
      <>
        <CallMini
          session={session}
          call={call}
          corner={corner}
          onMove={setCorner}
          onExpand={onExpand}
          onHangUp={hangUp}
          onFloat={float}
        >
          {floatingError}
        </CallMini>
        {confirm}
      </>
    );
  const ringing = call.status === "incoming";
  const videoActive =
    call.media === "video" ||
    call.sharingScreen ||
    call.remoteSharingScreen ||
    (!!call.localStream && !call.cameraOff) ||
    (!!call.remoteStream && !call.remoteCameraOff);
  const microphoneLevel = call.muted || call.microphoneStopped ? 0 : call.level;
  return (
    <Modal
      title={t(call.media === "video" ? "مكالمة فيديو" : "مكالمة صوتية")}
      onClose={ringing ? undefined : onMinimize}
      closeLabel={t("تصغير المكالمة")}
      closeIcon={<Minimize width={19} height={19} />}
      className={`voice-modal ${videoActive ? "video-modal" : ""} ${controlsIdle ? "controls-idle" : ""}`}
      allowFullscreen={videoActive}
      dismissible={false}
    >
      <span className="modal-kicker">
        <Wave /> {t(call.media === "video" ? "مكالمة فيديو" : "مكالمة صوتية")}
      </span>
      {videoActive ? (
        <CallVideo
          person={call.person}
          localStream={call.localStream}
          remoteStream={call.remoteStream}
          remoteAudio={call.remoteAudio}
          cameraOff={call.cameraOff}
          remoteCameraOff={call.remoteCameraOff}
          connected={call.status === "connected"}
          sharingScreen={call.sharingScreen}
          remoteSharingScreen={call.remoteSharingScreen}
        />
      ) : (
        <CallPortrait
          person={call.person}
          voice={call.status === "connected" ? call.remoteAudio : null}
        />
      )}
      <h2>{call.person.name}</h2>
      <p className="voice-state">
        {call.person.guest && <>{t("ضيف عبر رابطك")} · </>}
        <CallStatus call={call} />
      </p>
      {call.media === "video" && ringing && (
        <p className="video-consent">
          {t("لا تعمل كاميرتك إلا إذا رددت بالفيديو.")}
        </p>
      )}
      <CallWarnings session={session} call={call} />
      {/* While it rings the microphone is still off. */}
      {!ringing && <AudioMeter level={microphoneLevel} />}
      <div className={`call-controls ${!ringing ? "with-sharing" : ""}`}>
        {ringing ? (
          <>
            <button
              className="accept-call"
              onClick={() => void session.accept()}
            >
              <span>
                {call.media === "video" ? (
                  <Video width={22} height={22} />
                ) : (
                  <Phone width={22} height={22} />
                )}
              </span>
              {t(call.media === "video" ? "الرد بالفيديو" : "رد")}{" "}
            </button>
            {/* Each person decides on their own camera, whatever the caller chose. */}
            {call.media === "video" && (
              <button
                className="accept-call"
                onClick={() => void session.accept("audio")}
              >
                <span>
                  <Phone width={22} height={22} />
                </span>
                {t("صوت فقط")}
              </button>
            )}
          </>
        ) : (
          <>
            <CallControl
              label={call.muted ? t("إلغاء الكتم") : t("كتم الصوت")}
              className={
                call.muted ? "muted" : call.microphoneStopped ? "off" : ""
              }
              disabled={call.microphoneStopped}
              aria-pressed={call.muted}
              onClick={session.toggleMute}
            >
              {call.muted ? (
                <MicOff width={22} height={22} />
              ) : (
                <Mic width={22} height={22} />
              )}
            </CallControl>
            {(call.media === "video" || call.supportsScreenSharing) && (
              <CallControl
                label={t(
                  call.sharingScreen
                    ? "الكاميرا متوقفة"
                    : call.cameraOff
                      ? "تشغيل الكاميرا"
                      : "إيقاف الكاميرا",
                )}
                // A voice call's camera is off without anyone choosing that.
                className={
                  call.media === "video" && call.cameraOff ? "off" : ""
                }
                disabled={
                  call.cameraBusy || call.sharingScreen || !call.canSendVideo
                }
                aria-pressed={call.cameraOff}
                onClick={() => void session.toggleCamera()}
              >
                {call.cameraOff ? (
                  <VideoOff width={22} height={22} />
                ) : (
                  <Video width={22} height={22} />
                )}
              </CallControl>
            )}
            {call.canFlipCamera && (
              <CallControl
                label={t("تبديل الكاميرا")}
                disabled={call.cameraBusy}
                onClick={() => void session.flipCamera()}
              >
                <CameraRotate width={22} height={22} />
              </CallControl>
            )}
            {answered &&
              (call.media === "video" || call.supportsScreenSharing) && (
                <CallControl
                  label={t(
                    call.sharingScreen ? "إيقاف المشاركة" : "مشاركة الشاشة",
                  )}
                  className={call.sharingScreen ? "sharing" : ""}
                  disabled={
                    call.cameraBusy ||
                    call.status !== "connected" ||
                    !call.canShareScreen
                  }
                  hint={
                    !call.canShareScreen
                      ? t("هذا المتصفح لا يدعم مشاركة الشاشة.")
                      : undefined
                  }
                  aria-pressed={call.sharingScreen}
                  onClick={() =>
                    void (call.sharingScreen
                      ? session.stopScreenShare()
                      : session.startScreenShare())
                  }
                >
                  {call.sharingScreen ? (
                    <Square width={20} height={20} />
                  ) : (
                    <MonitorUp width={22} height={22} />
                  )}
                </CallControl>
              )}
            <CallControl
              label={t(
                call.deafened || call.playbackBlocked
                  ? "تشغيل الصوت"
                  : "كتم السماعة",
              )}
              className={call.deafened || call.playbackBlocked ? "off" : ""}
              aria-pressed={call.deafened}
              onClick={session.togglePlayback}
            >
              {call.deafened || call.playbackBlocked ? (
                <VolumeX width={22} height={22} />
              ) : (
                <Volume2 width={22} height={22} />
              )}
            </CallControl>
          </>
        )}
        <CallControl
          label={t(ringing ? "رفض" : "إنهاء")}
          iconOnly={!ringing}
          className="end-call"
          onClick={hangUp}
        >
          <PhoneOff width={22} height={22} />
        </CallControl>
      </div>
      {float && (
        <button
          className="icon-button call-float-button"
          aria-label={t("عرض فوق التبويبات")}
          title={t("عرض فوق التبويبات")}
          onClick={float}
        >
          <Float width={19} height={19} />
        </button>
      )}
      {floatingError}
      {/* Inside the call so it stays visible when the call is full screen. */}
      {confirm}
    </Modal>
  );
}

// The call's card stays for a moment with what is left to do. Moving over it or
// through its buttons keeps it up; left alone it goes by itself.
function CallEnded({
  ended,
  friend,
  corner,
  minimized,
  onClose,
  onCallAgain,
  onMessage,
}: {
  ended: EndedCall;
  friend: Person | undefined;
  corner: Corner;
  minimized: boolean;
  onClose: () => void;
  onCallAgain: (person: Person, media: CallMedia) => void;
  onMessage: (person: Person) => void;
}) {
  const [activity, setActivity] = useState(0);
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [activity, onClose]);
  const stay = () => setActivity((count) => count + 1);
  const staying = {
    onPointerMove: stay,
    onPointerDown: stay,
    onFocus: stay,
    onKeyDown: stay,
  };
  const { person, media, seconds } = ended;
  const title = t("انتهت المكالمة مع {name}", { name: person.name });
  const status = (
    <>
      {t("المكالمة انتهت")} · <span dir="ltr">{duration(seconds)}</span>
    </>
  );
  const CallIcon = media === "video" ? Video : Phone;
  if (minimized)
    return (
      <section
        className="call-mini call-mini-ended"
        data-corner={corner}
        aria-label={title}
        {...staying}
      >
        <div className="call-mini-bar">
          <Avatar person={person} />
          <p className="call-mini-text" role="status">
            <strong>{person.name}</strong>
            <span>{status}</span>
          </p>
          {friend && (
            <>
              <button
                className="icon-button"
                aria-label={t("راسل")}
                title={t("راسل")}
                onClick={() => onMessage(friend)}
              >
                <MessageCircle width={18} height={18} />
              </button>
              <button
                className="icon-button call-action"
                aria-label={t("اتصل مجددًا")}
                title={t("اتصل مجددًا")}
                onClick={() => onCallAgain(friend, media)}
              >
                <CallIcon width={18} height={18} />
              </button>
            </>
          )}
          <button
            className="icon-button"
            aria-label={t("إغلاق")}
            title={t("إغلاق")}
            onClick={onClose}
          >
            <X width={18} height={18} />
          </button>
        </div>
      </section>
    );
  return (
    <Modal title={title} onClose={onClose} className="voice-modal">
      <div className="call-ended" {...staying}>
        <span className="modal-kicker">
          <Wave /> {t(media === "video" ? "مكالمة فيديو" : "مكالمة صوتية")}
        </span>
        <CallPortrait person={person} voice={null} />
        <h2>{person.name}</h2>
        <p className="voice-state" role="status">
          {status}
        </p>
        {friend && (
          <div className="call-controls">
            <CallControl
              label={t("اتصل مجددًا")}
              iconOnly={false}
              className="accept-call"
              onClick={() => onCallAgain(friend, media)}
            >
              <CallIcon width={22} height={22} />
            </CallControl>
            <CallControl
              label={t("راسل")}
              iconOnly={false}
              onClick={() => onMessage(friend)}
            >
              <MessageCircle width={22} height={22} />
            </CallControl>
          </div>
        )}
      </div>
    </Modal>
  );
}

function CallStatus({ call }: { call: Call }) {
  if (call.status === "connected")
    return (
      <>
        <span dir="ltr">
          {Math.floor(call.seconds / 60)
            .toString()
            .padStart(2, "0")}
          :{(call.seconds % 60).toString().padStart(2, "0")}
        </span>
        <span className="peer-muted" role="status">
          {call.remoteMuted &&
            t("صوت {name} مكتوم", { name: call.person.name })}
        </span>
      </>
    );
  if (call.status === "reconnecting") return t("جارٍ استعادة الاتصال…");
  if (call.status === "starting")
    return t(
      call.media === "video"
        ? "جارٍ تشغيل الكاميرا والميكروفون…"
        : "جارٍ تشغيل الميكروفون…",
    );
  if (call.status === "connecting")
    return t(
      call.media === "video" ? "جارٍ توصيل الفيديو…" : "جارٍ توصيل الصوت…",
    );
  return t(call.status === "incoming" ? "مكالمة واردة…" : "جارٍ الاتصال…");
}

function CallWarnings({ session, call }: { session: CallSession; call: Call }) {
  if (!call.microphoneStopped && !call.playbackBlocked) return null;
  return (
    <div className="call-warnings">
      {call.microphoneStopped && (
        <div className="call-warning" role="alert">
          <span>{t("توقف الميكروفون. لا يستطيع الطرف الآخر سماعك.")}</span>
          <button
            disabled={call.microphoneBusy}
            onClick={() => void session.restartMicrophone()}
          >
            {t(
              call.microphoneBusy
                ? "جارٍ إعادة تشغيل الميكروفون…"
                : "إعادة تشغيل الميكروفون",
            )}
          </button>
        </div>
      )}
      {call.playbackBlocked && (
        <div className="call-warning" role="alert">
          {t(
            "تعذر تشغيل صوت الطرف الآخر. اضغط على «تشغيل الصوت» للمحاولة مجددًا.",
          )}
        </div>
      )}
    </div>
  );
}

// Slide from where a drag let go into the corner the card snapped to.
function glide(node: HTMLElement, from: DOMRect) {
  const to = node.getBoundingClientRect();
  node.animate(
    [
      {
        transform: `translate(${from.left - to.left}px, ${from.top - to.top}px)`,
      },
      { transform: "none" },
    ],
    { duration: 300, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
  );
}

function CallMini({
  session,
  call,
  corner,
  onMove,
  onExpand,
  onHangUp,
  onFloat,
  floating = false,
  children,
}: {
  session: CallSession;
  call: Call;
  corner: Corner;
  onMove: (corner: Corner) => void;
  onExpand: () => void;
  onHangUp: () => void;
  onFloat?: () => void;
  floating?: boolean;
  children?: ReactNode;
}) {
  const panel = useRef<HTMLElement>(null),
    expand = useRef<HTMLButtonElement>(null),
    drag = useRef<{ x: number; y: number; moved: boolean } | null>(null),
    dropped = useRef<DOMRect | null>(null);
  useEffect(() => {
    // Minimizing returns focus to the call's opener; keep it here when that is gone.
    if (!floating && document.activeElement === document.body)
      expand.current?.focus();
  }, [floating]);
  useLayoutEffect(() => {
    if (panel.current && dropped.current) glide(panel.current, dropped.current);
    dropped.current = null;
  }, [corner]);
  function grab(event: PointerEvent<HTMLElement>) {
    if (
      floating ||
      matchMedia("(max-width: 900px)").matches ||
      event.button !== 0 ||
      (event.target as Element).closest("button")
    )
      return;
    drag.current = { x: event.clientX, y: event.clientY, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  function move(event: PointerEvent<HTMLElement>) {
    const start = drag.current;
    if (!start) return;
    const x = event.clientX - start.x,
      y = event.clientY - start.y;
    if (!start.moved && Math.hypot(x, y) < 6) return;
    start.moved = true;
    event.currentTarget.style.transform = `translate(${x}px, ${y}px)`;
  }
  function drop(event: PointerEvent<HTMLElement>) {
    const moved = drag.current?.moved;
    drag.current = null;
    if (!moved) return;
    const node = event.currentTarget,
      box = node.getBoundingClientRect();
    node.style.transform = "";
    const right = box.left + box.width / 2 > innerWidth / 2,
      rtl = getComputedStyle(node).direction === "rtl";
    const next: Corner = `${box.top + box.height / 2 > innerHeight / 2 ? "bottom" : "top"}-${right === rtl ? "start" : "end"}`;
    if (next === corner) glide(node, box);
    else {
      dropped.current = box;
      onMove(next);
    }
  }
  const showingRemote =
    call.status === "connected" && !!call.remoteStream && !call.remoteCameraOff;
  return (
    <section
      ref={panel}
      className="call-mini"
      data-corner={corner}
      aria-label={t("المكالمة مع {name}", { name: call.person.name })}
      onPointerDown={grab}
      onPointerMove={move}
      onPointerUp={drop}
      onPointerCancel={drop}
    >
      {showingRemote && (
        <div className="call-mini-video">
          <VideoPreview
            stream={call.remoteStream}
            label={t(
              call.remoteSharingScreen ? "شاشة {name}" : "فيديو {name}",
              { name: call.person.name },
            )}
          />
        </div>
      )}
      <div className="call-mini-bar">
        {!showingRemote && <Avatar person={call.person} />}
        <p className="call-mini-text">
          <strong>{call.person.name}</strong>
          <span>
            <CallStatus call={call} />
            <Expand
              className="call-mini-mobile-expand"
              width={12}
              height={12}
            />
          </span>
        </p>
        <button
          className={`icon-button ${call.muted ? "muted" : call.microphoneStopped ? "off" : ""}`}
          disabled={call.microphoneStopped}
          aria-label={call.muted ? t("إلغاء الكتم") : t("كتم الصوت")}
          title={call.muted ? t("إلغاء الكتم") : t("كتم الصوت")}
          onClick={session.toggleMute}
        >
          {call.muted ? (
            <MicOff width={18} height={18} />
          ) : (
            <Mic width={18} height={18} />
          )}
        </button>
        <button
          ref={expand}
          className="icon-button call-mini-expand"
          aria-label={t("العودة إلى المكالمة")}
          title={t("العودة إلى المكالمة")}
          onClick={onExpand}
        >
          <Expand width={18} height={18} />
        </button>
        {onFloat && (
          <button
            className="icon-button call-mini-float"
            aria-label={t("عرض فوق التبويبات")}
            title={t("عرض فوق التبويبات")}
            onClick={onFloat}
          >
            <Float width={18} height={18} />
          </button>
        )}
        <button
          className="icon-button end-call"
          aria-label={t("إنهاء")}
          title={t("إنهاء")}
          onClick={onHangUp}
        >
          <PhoneOff width={18} height={18} />
        </button>
      </div>
      <CallWarnings session={session} call={call} />
      {children}
    </section>
  );
}
